import { useCallback, useId, type ReactNode } from 'react'

/*
 * Small SVG helpers with no kit imports: home cards (each page's CardArt.tsx) are drawn with the app
 * on the home screen, before any page or the kit's UI has loaded.
 */

/**
 * Ids for an SVG's gradients and filters, unique per mounted copy (a card or icon drawn twice on
 * one screen would otherwise share, and fight over, one id): `const id = useSvgIds()`, then
 * `<radialGradient id={id('chrome')}>` and `fill={`url(#${id('chrome')})`}`.
 */
export function useSvgIds(): (name: string) => string {
  const prefix = useId().replace(/[^a-zA-Z0-9_-]/g, '')
  return useCallback((name: string) => `${prefix}-${name}`, [prefix])
}

/**
 * A home card's picture: the 320 x 200 card frame, sliced to fill, on a background colour. For
 * gradients, children can be a function of the card's own ids (`useSvgIds`).
 */
export function CardSvg({ bg, children }: { bg: string; children: ReactNode | ((id: (name: string) => string) => ReactNode) }) {
  const id = useSvgIds()
  return (
    <svg viewBox="0 0 320 200" className="absolute inset-0 h-full w-full" preserveAspectRatio="xMidYMid slice">
      <rect width="320" height="200" fill={bg} />
      {typeof children === 'function' ? children(id) : children}
    </svg>
  )
}
